import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * Which email cells of the Workspace grid have a run in flight, and (for runs
 * started before the bulk endpoint existed) which workflow is filling which cell.
 *
 * In Redux rather than in component state for one
 * concrete reason: a column-wide run adopts one workflow per row, so this map
 * changes once per row — up to a thousand times in a few seconds. Passed down
 * as a prop it was a dependency of workspace-table's column-def `useMemo`, so
 * every single adoption rebuilt every column definition, which hands
 * SearchTable a new `columns` array, which invalidates its `nonStickyColumns`
 * memo, which re-runs its width-distribution effect and `setColumnWidths`.
 * The whole grid re-laid-out on every row, and that read as the table
 * flickering for the length of the run.
 *
 * Here, each cell selects only its own entry, so an adoption re-renders that
 * one cell and nothing else.
 */
export interface WorkspaceEmailState {
  /** `${docId}:${columnName}` -> workflowId. */
  workflowIdByCell: Record<string, string>;
  /** Columns whose run is still dispatching creates — their not-yet-started cells read as queued. */
  runningColumns: string[];
  /**
   * Bulk runs by batch id. Recorded when sendBulkEmail resolves (or when an
   * externally-started batch is adopted from its first SSE event), so the SSE
   * handler can turn that batch's `workflow.finished` events — which carry
   * only `bulkBatchId` + `workflowId`, no sheet or column — into rows
   * refreshes for the right sheet.
   *
   * `total` is workflows actually started (the ack's `total` — duplicates
   * emit no events, so they're excluded; null when unknown, i.e. adopted
   * runs); every finished event ticks `completed`. While the entry lives, the
   * column header shows live progress and valueless cells read as queued.
   */
  bulkRunsByBatchId: Record<
    string,
    { userUploadId: string; columnName: string; total: number | null; completed: number }
  >;
}

const initialState: WorkspaceEmailState = {
  workflowIdByCell: {},
  runningColumns: [],
  bulkRunsByBatchId: {},
};

/** The key both this slice and the grid address a cell by. */
export function emailCellKey(docId: string, columnName: string): string {
  return `${docId}:${columnName}`;
}

const workspaceEmailSlice = createSlice({
  name: 'workspaceEmail',
  initialState,
  reducers: {
    emailWorkflowAdopted(state, action: PayloadAction<{ cellKey: string; workflowId: string }>) {
      state.workflowIdByCell[action.payload.cellKey] = action.payload.workflowId;
    },
    emailColumnRunStarted(state, action: PayloadAction<string>) {
      if (!state.runningColumns.includes(action.payload)) state.runningColumns.push(action.payload);
    },
    emailColumnRunFinished(state, action: PayloadAction<string>) {
      state.runningColumns = state.runningColumns.filter((name) => name !== action.payload);
    },
    /**
     * Records which sheet a bulk email run belongs to. Dispatched once when
     * sendBulkEmail resolves with the bulk tracker id — read back by the SSE
     * handler for every `workflow.finished` of that batch. Preserves the
     * `completed` count: adoption can race finished events (external runs tick
     * before the resolving snapshot lands), and wiping it would drag the
     * header progress backwards.
     */
    emailBulkStarted(
      state,
      action: PayloadAction<{
        bulkBatchId: string;
        userUploadId: string;
        columnName: string;
        total?: number | null;
      }>
    ) {
      const prev = state.bulkRunsByBatchId[action.payload.bulkBatchId];
      state.bulkRunsByBatchId[action.payload.bulkBatchId] = {
        userUploadId: action.payload.userUploadId,
        columnName: action.payload.columnName,
        total: action.payload.total ?? prev?.total ?? null,
        completed: prev?.completed ?? 0,
      };
    },
    /**
     * One `workflow.finished` for the batch. Creates a nameless shell when
     * the batch isn't mapped yet (external run whose resolving snapshot is
     * still in flight) — invisible to the header until adoption fills in the
     * names. Self-deletes on completion so a finished run stops occupying
     * the header; stragglers past that recreate an invisible shell, never a
     * stuck spinner.
     */
    emailBulkTick(state, action: PayloadAction<string>) {
      const entry = state.bulkRunsByBatchId[action.payload] ?? {
        userUploadId: '',
        columnName: '',
        total: null,
        completed: 0,
      };
      entry.completed += 1;
      if (entry.total !== null && entry.completed >= entry.total) {
        delete state.bulkRunsByBatchId[action.payload];
        return;
      }
      state.bulkRunsByBatchId[action.payload] = entry;
    },
    /** Drops a batch's progress, e.g. once its post-run rows refresh fires. */
    emailBulkCleared(state, action: PayloadAction<string>) {
      delete state.bulkRunsByBatchId[action.payload];
    },
    /**
     * Clears the in-flight run flags, for a sheet switch.
     *
     * `runningColumns` holds column NAMES, which are not unique across sheets
     * — two sheets each with an "Email Enrich" column would show each other's
     * "Queued…" state — so these must go.
     *
     * `workflowIdByCell` deliberately survives: its keys start with a
     * workspace_data document id, which is unique per row across every sheet,
     * so an entry can only ever be read by the row it belongs to. Keeping it
     * means leaving a sheet mid-run and coming back still shows each cell's
     * live progress instead of a grid of dashes.
     */
    emailRunFlagsCleared(state) {
      state.runningColumns = [];
    },
  },
});

export const {
  emailWorkflowAdopted,
  emailColumnRunStarted,
  emailColumnRunFinished,
  emailBulkStarted,
  emailBulkTick,
  emailBulkCleared,
  emailRunFlagsCleared,
} = workspaceEmailSlice.actions;

/**
 * The workflow filling one cell, or undefined. A plain property read, so it's
 * referentially stable and needs no memoized selector — which matters when
 * every email cell in the grid calls it on every store change.
 */
export const selectEmailWorkflowId = (state: RootState, cellKey: string): string | undefined =>
  state.workspaceEmail.workflowIdByCell[cellKey];

export const selectIsEmailColumnRunning = (state: RootState, columnName: string): boolean =>
  state.workspaceEmail.runningColumns.includes(columnName);

/** The live bulk run for one sheet column, if any. Completed runs delete
 * their own entries, so a hit here is always still in flight. */
export const selectBulkRun = (
  state: RootState,
  sheetId: string,
  columnName: string
): { userUploadId: string; columnName: string; total: number | null; completed: number } | undefined => {
  // Object.keys + index access rather than Object.values: this toolchain
  // types the latter's result as unknown[] even over a concrete Record.
  const runs = state.workspaceEmail.bulkRunsByBatchId;
  for (const batchId of Object.keys(runs)) {
    const run = runs[batchId];
    if (run.userUploadId === sheetId && run.columnName === columnName) return run;
  }
  return undefined;
};

/** Whether the sheet's column has a bulk run currently in flight. */
export const selectIsBulkColumnRunning = (state: RootState, sheetId: string, columnName: string): boolean =>
  selectBulkRun(state, sheetId, columnName) !== undefined;

export default workspaceEmailSlice.reducer;
